import { c as _c } from "react/compiler-runtime";
import { Skeleton } from "@flat-ui";
import { useErrorContext } from "@context-providers/error-boundary/error-handling-context";
import { AccountSettingsDropdown } from "@pages/account-settings/account-settings-dropdown";
import { SettingsApiTypes } from "@stellar/settings-api-client";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { loggedInUserSelector } from "@store/user/user-selector";
import { UserProfile } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { useEffect, useState } from "react";
import { getUnitHeader } from "@pages/account-settings/account-settings-utils";
import { BrowserUtils } from "@stellar/utils";
import { transmitSettingsToParent } from "@utils/account-settings-utils";
import { fetchingUserSettingSelector, userSettingsSelector } from "@store/settings/settings-selector";
import { updateUserSetting } from "@store/settings/settings-thunks";
/**
 * All the unit types mapped to their description
 * "metric" and "imperial" is allowed to have them as subheader
 */
const units = {
  metric: {
    isHeader: true,
    // Using meters as value here just to be allowed in the list as subheader, no use of this value
    value: "meters",
    label: "METRIC"
  },
  meters: {
    value: "meters",
    label: "Meters",
    description: "(2.345 m)"
  },
  millimeters: {
    value: "millimeters",
    label: "Millimeters",
    description: "(2345 mm)"
  },
  imperial: {
    isHeader: true,
    // Using decimalFeet as value here just to be allowed in the list as subheader, no use of this value
    value: "decimalFeet",
    label: "IMPERIAL"
  },
  feetFractionalInches: {
    value: "feetFractionalInches",
    label: "Feet + fractional inches",
    description: "(7' 8 5/16\")"
  },
  decimalFeet: {
    value: "decimalFeet",
    label: "Decimal feet",
    description: "(7.694')"
  },
  decimalUSSurveyFeet: {
    value: "decimalUSSurveyFeet",
    label: "Decimal US survey feet",
    description: "(7.694 ft US)"
  },
  fractionalInches: {
    value: "fractionalInches",
    label: "Fractional inches",
    description: '(92 5/16")'
  },
  decimalInches: {
    value: "decimalInches",
    label: "Decimal inches",
    description: '(92.32")'
  }
};

/** A simple unit dropdown. When opened the user can chose between the available units */
export function AccountUnitSelector() {
  const $ = _c(22);
  const {
    trackEvent
  } = useTrackEvent();
  const loggedInUser = useAppSelector(loggedInUserSelector);
  const [selectedUnit, setSelectedUnit] = useState(units.meters);
  const [updatedModifiedAt, setUpdatedModifiedAt] = useState("0");
  const {
    handleErrorWithToast
  } = useErrorContext();
  const userSettings = useAppSelector(userSettingsSelector);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = fetchingUserSettingSelector("unit_measurement");
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const fetchingUnitMeasurementSettings = useAppSelector(t0);
  const dispatch = useAppDispatch();
  let t1;
  let t2;
  if ($[1] !== loggedInUser || $[2] !== userSettings) {
    t1 = () => {
      if (!userSettings?.unit_measurement) {
        if (!loggedInUser) {
          const localUnit = localStorage.getItem("unit_measurement");
          if (localUnit && SettingsApiTypes.isValidUnitMeasurement(localUnit)) {
            setSelectedUnit(units[localUnit]);
          }
        }
        return;
      }
      const {
        value,
        modifiedAt
      } = userSettings.unit_measurement;
      setSelectedUnit(units[value]);
      setUpdatedModifiedAt(modifiedAt);
    };
    t2 = [loggedInUser, userSettings];
    $[1] = loggedInUser;
    $[2] = userSettings;
    $[3] = t1;
    $[4] = t2;
  } else {
    t1 = $[3];
    t2 = $[4];
  }
  useEffect(t1, t2);
  let t3;
  if ($[5] !== dispatch || $[6] !== handleErrorWithToast || $[7] !== loggedInUser || $[8] !== trackEvent || $[9] !== updatedModifiedAt) {
    t3 = async function onUnitChange(newUnit) {
      trackEvent({
        name: UserProfile.changeUnit,
        props: {
          unit: newUnit,
          isUserLoggedIn: !!loggedInUser
        }
      });
      localStorage.setItem("unit_measurement", newUnit);
      if (BrowserUtils.isInIframe()) {
        transmitSettingsToParent({
          settingIdentifier: "unit_measurement",
          settingValue: newUnit
        });
      }
      if (!loggedInUser) {
        setSelectedUnit(units[newUnit]);
        return;
      }
      ;
      try {
        const response = await dispatch(updateUserSetting({
          identifier: "unit_measurement",
          userId: loggedInUser.id,
          payload: {
            value: newUnit,
            modifiedAt: updatedModifiedAt
          }
        })).unwrap();
        setSelectedUnit(units[response.setting.value]);
        setUpdatedModifiedAt(response.setting.modifiedAt);
      } catch (t4) {
        const error = t4;
        handleErrorWithToast({
          id: `updateUnit-${Date.now().toString()}`,
          title: "Failed to update the unit setting.",
          error
        });
      }
    };
    $[5] = dispatch;
    $[6] = handleErrorWithToast;
    $[7] = loggedInUser;
    $[8] = trackEvent;
    $[9] = updatedModifiedAt;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const onUnitChange = t3;
  if (fetchingUnitMeasurementSettings === "pending") {
    let t4;
    if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <Skeleton variant="rectangular" width="100%" />;
      $[11] = t4;
    } else {
      t4 = $[11];
    }
    return t4;
  }
  let t4;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = Object.values(units);
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  let t5;
  if ($[13] !== selectedUnit.value) {
    t5 = getUnitHeader(selectedUnit.value);
    $[13] = selectedUnit.value;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  const t6 = `${t5} / ${selectedUnit.label}`;
  let t7;
  if ($[15] !== selectedUnit.description || $[16] !== selectedUnit.value || $[17] !== t6) {
    t7 = {
      label: t6,
      value: selectedUnit.value,
      description: selectedUnit.description
    };
    $[15] = selectedUnit.description;
    $[16] = selectedUnit.value;
    $[17] = t6;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[19] !== onUnitChange || $[20] !== t7) {
    t8 = <AccountSettingsDropdown items={t4} selectedItem={t7} onClickHandler={onUnitChange} />;
    $[19] = onUnitChange;
    $[20] = t7;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  return t8;
}